// Template interpolation utility (template-update branch).
//
// Scope: pure string transformation for rendering email/SMS template bodies
// and subjects. Intentionally has no runtime consumers yet — it will be wired
// from SendListingsDialog and the ClientList SMS action once migration 007
// lands and the Templates CRUD UI ships.
//
// Behavior contract (mirrors migration 007 header):
// - Missing context keys resolve to "" (empty string), NEVER the literal
//   string "undefined". This is the fail-soft guarantee send flows depend on.
// - Unknown placeholders (not present in context) are also replaced with "".
//   Leaving "{{foo}}" visible in a sent message is a worse failure mode than
//   silently dropping it.
// - Placeholders are case-sensitive and whitespace-tolerant inside the braces:
//   "{{ client_first_name }}" and "{{client_first_name}}" both resolve.
// - Malformed/unsupported double-brace tokens are still consumed as "" so raw
//   template syntax does not leak into sent messages. Save-time validation can
//   reject unsupported keys before users reach send flows.
// - Non-string context values are coerced via String(); null/undefined -> "".

/**
 * Declared variable namespace for v1 template interpolation.
 *
 * Kept as a shared type so send flows, preview rendering, and future
 * variable-validation UI all agree on the same key set. Additions here
 * must be coordinated with migration 007's `variables` jsonb column.
 */
export interface TemplateContext {
  // Client fields
  client_first_name?: string;
  client_last_name?: string;
  client_full_name?: string;

  // Agent fields
  agent_first_name?: string;
  agent_full_name?: string;
  agent_email?: string;
  agent_phone?: string;

  // Selection / send-flow fields
  property_count?: number | string;
  first_property_name?: string;
  budget_range?: string;

  // System fields
  today_date?: string;
  unsubscribe_link?: string;

  // Instagram DM intake fields (channel=instagram_dm).
  // Added by migration 026 + PKT-0033. instagram_handle / reel_property_name /
  // source_post_id are populated by the webhook receiver (PKT-0036) from the
  // inbound DM payload. captured_fields / missing_field are populated by the
  // bot flow runner (PKT-0034) when rendering the intake_followup_missing
  // branch B template. client_email is populated from the captured intake
  // state when rendering the handoff_close template.
  instagram_handle?: string;
  reel_property_name?: string;
  source_post_id?: string;
  captured_fields?: string;
  missing_field?: string;
  client_email?: string;
}

export type TemplateContextKey = keyof TemplateContext;

/**
 * Runtime-visible enumeration of every declared TemplateContext key.
 *
 * TypeScript types are erased at runtime, so save paths / validation
 * UI / service layer code need a plain-data source of truth for the
 * v1 namespace. Keep this list in strict sync with TemplateContext --
 * the compile-time assertions immediately below will fail to build
 * if the list and the interface drift apart.
 *
 * Additions must also land in migration 007's `variables` jsonb
 * column design before being considered "released" for end users.
 */
export const TEMPLATE_CONTEXT_KEYS = [
  "client_first_name",
  "client_last_name",
  "client_full_name",
  "agent_first_name",
  "agent_full_name",
  "agent_email",
  "agent_phone",
  "property_count",
  "first_property_name",
  "budget_range",
  "today_date",
  "unsubscribe_link",
  // Instagram DM intake fields (added by PKT-0033 / migration 026).
  "instagram_handle",
  "reel_property_name",
  "source_post_id",
  "captured_fields",
  "missing_field",
  "client_email",
] as const;

// Compile-time guard #1: every entry must be a real TemplateContext key.
// A typo here (e.g. "client_firts_name") triggers a type error on this line.
const _onlyDeclaredKeys: ReadonlyArray<TemplateContextKey> =
  TEMPLATE_CONTEXT_KEYS;
void _onlyDeclaredKeys;

// Compile-time guard #2: the list must cover every TemplateContext key.
// Adding a new field to TemplateContext without updating the list turns
// the Exclude<> below into a non-never type, causing this line to fail.
type _MissingFromList = Exclude<
  TemplateContextKey,
  (typeof TEMPLATE_CONTEXT_KEYS)[number]
>;
const _listExhaustive: [_MissingFromList] extends [never] ? true : never = true;
void _listExhaustive;

const PLACEHOLDER_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;

function escapeHtmlValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value)
    .normalize("NFC")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Replace {{placeholder}} tokens in `input` with values from `context`.
 *
 * Pure function, safe to call with any string — non-template strings pass
 * through unchanged. See file-level contract for fail-soft guarantees.
 */
export function interpolateTemplate(
  input: string,
  context: TemplateContext
): string {
  if (!input) return "";
  return input.replace(PLACEHOLDER_PATTERN, (_match, rawKey: string) => {
    const key = rawKey.trim();
    const value = (context as Record<string, unknown>)[key];
    if (value === null || value === undefined) return "";
    return String(value);
  });
}

/**
 * HTML-safe variant of interpolateTemplate.
 *
 * Use for template output that will be embedded directly into HTML.
 * Plain-text channels such as SMS should continue using interpolateTemplate.
 */
export function interpolateTemplateHtml(
  input: string,
  context: TemplateContext
): string {
  if (!input) return "";
  return input.replace(PLACEHOLDER_PATTERN, (_match, rawKey: string) => {
    const key = rawKey.trim();
    const value = (context as Record<string, unknown>)[key];
    return escapeHtmlValue(value);
  });
}

/**
 * Returns the set of placeholder keys referenced by a template string.
 * Useful for variable-validation UI ("this template uses X but no value
 * was supplied") and for populating migration 007's `variables` column
 * on save. Order preserved, duplicates removed.
 *
 * NOTE: this returns BOTH valid identifier-shape keys AND malformed tokens
 * (e.g. "client-first-name", "client.first_name") so validation UI can
 * surface them as author errors.
 *
 * Callers that persist the result to migration 007's `variables` jsonb
 * column MUST filter with `isKnownTemplateVariableKey` (membership-based)
 * -- NOT `isIdentifierShapedTemplateToken` alone, which would accept
 * identifier-shaped unknowns like "not_a_real_key" and leak them into
 * storage. Save-path filtering is intentionally NOT done here so
 * validation layers retain full visibility into the raw token set.
 */
export function extractTemplateVariables(input: string): string[] {
  if (!input) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const match of input.matchAll(PLACEHOLDER_PATTERN)) {
    const key = match[1].trim();
    if (!key) continue;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

/**
 * Shape check only: matches the identifier grammar `[a-zA-Z0-9_]+`.
 *
 * Answers "could this string syntactically be a template variable key?"
 * NOT "is this an allowed template variable?" -- use
 * `isKnownTemplateVariableKey` for the latter. This predicate is useful
 * for separating author typos like "client-first-name" from structured
 * tokens, but it will happily accept an unknown identifier like
 * "not_a_real_key". Don't use it alone to gate DB writes.
 */
export function isIdentifierShapedTemplateToken(key: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(key);
}

/**
 * Membership check: is `key` one of the declared v1 TemplateContext
 * variables (see TEMPLATE_CONTEXT_KEYS)?
 *
 * This is the correct predicate for save-path filtering into migration
 * 005's `variables` jsonb column and for validation UI that must reject
 * unknown identifiers. Narrower than shape: "not_a_real_key" is
 * identifier-shaped but not a known key, and this returns false.
 *
 * Narrows the type to TemplateContextKey for downstream type safety.
 */
export function isKnownTemplateVariableKey(
  key: string
): key is TemplateContextKey {
  return (TEMPLATE_CONTEXT_KEYS as ReadonlyArray<string>).includes(key);
}

/**
 * Render both fields of a template with the same context in one call.
 *
 * Thin composition over `interpolateTemplate`, returned as a shaped
 * object so preview panes and send-time renderers both consume the
 * same contract. Empty subject is valid — migration 007 notes allow
 * SMS-channel templates to carry an empty subject while still sharing
 * this table. No side effects, no I/O.
 */
export function renderTemplatePreview(
  subject: string,
  body: string,
  context: TemplateContext
): { subject: string; body: string } {
  return {
    subject: interpolateTemplate(subject, context),
    body: interpolateTemplate(body, context),
  };
}

/**
 * HTML-safe preview helper. Subject stays plain-text; body is escaped for
 * direct embedding in HTML email markup.
 */
export function renderTemplatePreviewHtml(
  subject: string,
  body: string,
  context: TemplateContext
): { subject: string; body: string } {
  return {
    subject: interpolateTemplate(subject, context),
    body: interpolateTemplateHtml(body, context),
  };
}
